///
/// EarthBrowser
///
/// Created by Matt Giger on 12/6/2025
/// Copyright (c) 2025 Geodata Labs
///

import { EntityPanHandler } from "@/earthbrowser/datasets/EntityPanHandler"
import { type Camera } from "@stovokor/camera/Camera"
import { type GestureNode } from "@stovokor/gestures/GestureNode"
import { type EarthBrowserContext } from "../EarthBrowserContext"
import { EntityCameraState } from "./EntityCameraState"
import { EntityHoverHandler } from "./EntityHoverHandler"
import { EntityTapHandler } from "./EntityTapHandler"
import { EntityPinchHandler, EntityZoomHandler } from "./EntityZoomHandler"
import { type CameraState } from "@stovokor/camera/CameraState"
import { trig } from "@stovokor/math/math"
import { spherical } from "../utils/spherical"

export class EntityCameraAdapter {
	private context: EarthBrowserContext
	private entity: GestureNode
	private cameraState: EntityCameraState
	private tapHandler?: EntityTapHandler
	private hoverHandler?: EntityHoverHandler
	private panHandler?: EntityPanHandler
	private zoomHandler?: EntityZoomHandler
	private pinchHandler?: EntityPinchHandler

	constructor(context: EarthBrowserContext, entity: GestureNode, focusDistance: number, maxZoomRadius: number) {
		this.context = context
		this.entity = entity

		const initialSpherical = new spherical(focusDistance, -trig.half_pi, trig.half_pi / 6)
		this.cameraState = new EntityCameraState(context, entity, maxZoomRadius, initialSpherical)

		this.tapHandler = new EntityTapHandler(context)
		this.entity.addRecognizer(this.tapHandler)
		this.tapHandler.onGesture(() => {
			this.context.focusManager.requestFocus(this.entity)
		})

		this.hoverHandler = new EntityHoverHandler(context)
		this.entity.addRecognizer(this.hoverHandler)
	}

	public attach(camera: Camera): void {
		this.cameraState.activate(camera)

		this.panHandler = new EntityPanHandler(this.context, this.cameraState)
		this.entity.addRecognizer(this.panHandler)
		this.zoomHandler = new EntityZoomHandler(this.context, this.cameraState)
		this.entity.addRecognizer(this.zoomHandler)
		this.pinchHandler = new EntityPinchHandler(this.context, this.cameraState)
		this.entity.addRecognizer(this.pinchHandler)
	}

	public update(): void {
		this.cameraState.renderUpdate()
	}

	public detach(): void {
		this.cameraState.deactivate()

		this.entity.removeRecognizer(this.panHandler)
		this.panHandler = undefined
		this.entity.removeRecognizer(this.zoomHandler)
		this.zoomHandler = undefined
		this.entity.removeRecognizer(this.pinchHandler)
		this.pinchHandler = undefined
	}

	public getTargetCameraState(camera?: Camera): CameraState {
		return this.cameraState.getTargetCameraState(camera)
	}

	public animatedTiltAndBearing(tilt: number, bearing: number): boolean {
		this.cameraState.animatedTiltAndBearing(tilt, bearing)
		return true
	}

	public animatedCameraBearing(bearing: number): boolean {
		this.cameraState.animatedCameraBearing(bearing)
		return true
	}

	public animatedCameraTilt(tilt: number): boolean {
		this.cameraState.animatedCameraTilt(tilt)
		return true
	}

	public immediateCameraTilt(tilt: number): void {
		this.cameraState.immediateCameraTilt(tilt)
	}

	public immediateCameraBearing(bearing: number): void {
		this.cameraState.immediateCameraBearing(bearing)
	}

	/**
	 * Handle camera and compass commands
	 * This centralizes command handling for all entities using this adapter
	 */
	public handleCommand(command: string, data?: unknown): boolean {
		switch (command) {
			case "compass/reset":
				this.animatedTiltAndBearing(0, 0)
				return true

			case "compass/setTiltBearing":
				if (data && typeof data === "object" && "tilt" in data && "bearing" in data) {
					const { tilt, bearing } = data as { tilt: number; bearing: number }
					return this.animatedTiltAndBearing(tilt, bearing)
				}
				return false

			case "camera/setBearing":
				if (typeof data === "number") {
					return this.animatedCameraBearing(data)
				}
				return false

			case "camera/setTilt":
				if (typeof data === "number") {
					return this.animatedCameraTilt(data)
				}
				return false

			case "camera/setTiltImmediate":
				if (typeof data === "number") {
					this.immediateCameraTilt(data)
					return true
				}
				return false

			case "camera/setBearingImmediate":
				if (typeof data === "number") {
					this.animatedCameraBearing(data)	// Do it animated
					return true
				}
				return false

			default:
				return false
		}
	}
}